import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  MapPin, 
  Calendar, 
  Clock, 
  Users, 
  Trophy,
  AlertTriangle,
  CheckCircle,
  Building,
  Phone,
  Mail
} from 'lucide-react';
import { useUserRole } from '@/hooks/useUserRole';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { formatGameDateTime } from '@/utils/timezone';

interface VenueInfo {
  venue_id: string;
  venue_name: string;
  venue_address?: string;
  phone?: string;
  email?: string;
  city?: string;
  state_province?: string;
}

interface GameDetailsCardProps {
  gameId: string;
  gameData: any;
  showVenueInfo?: boolean;
}

export const GameDetailsCard: React.FC<GameDetailsCardProps> = ({ 
  gameId, 
  gameData, 
  showVenueInfo = true 
}) => {
  const { permissions, userId } = useUserRole();
  const [venueInfo, setVenueInfo] = useState<VenueInfo | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (showVenueInfo && gameData?.venue_id) {
      loadVenueInfo();
    }
  }, [gameData?.venue_id, showVenueInfo]);

  const loadVenueInfo = async () => {
    if (!gameData?.venue_id) return;

    setLoading(true);
    try {
      const { data: venue, error } = await supabase
        .from('venues')
        .select('venue_id, venue_name, venue_address, phone, email, city, state_province')
        .eq('venue_id', gameData.venue_id)
        .maybeSingle();

      if (error) {
        console.error('Error loading venue info:', error);
        return;
      }

      if (venue) {
        setVenueInfo(venue);
      }
    } catch (error) {
      console.error('Error fetching venue details:', error);
    } finally {
      setLoading(false);
    }
  };

  const getGameStatusBadge = (status: string) => {
    const statusConfig = {
      'pending': { variant: 'secondary' as const, icon: Clock, color: 'text-yellow-600' },
      'confirmed': { variant: 'default' as const, icon: CheckCircle, color: 'text-green-600' },
      'in_progress': { variant: 'default' as const, icon: Trophy, color: 'text-blue-600' },
      'completed': { variant: 'outline' as const, icon: CheckCircle, color: 'text-gray-600' },
      'cancelled': { variant: 'destructive' as const, icon: AlertTriangle, color: 'text-red-600' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className={`h-3 w-3 ${config.color}`} />
        {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
      </Badge>
    );
  };

  return (
    <Card className="bg-card border-border">
      <CardHeader className="pb-4">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-foreground text-lg flex items-center gap-2">
              <Trophy className="h-5 w-5 text-primary" />
              {gameData.game_title || gameData.game_name}
            </CardTitle>
            {gameData.game_code && (
              <p className="text-sm text-muted-foreground mt-1">
                Game Code: <span className="font-mono font-medium">{gameData.game_code}</span>
              </p>
            )}
          </div>
          {getGameStatusBadge(gameData.game_status || gameData.status)}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Date & Time */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Calendar className="h-4 w-4 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Date</p>
              <p className="font-medium text-foreground">
                {(() => {
                  try {
                    const formatted = formatGameDateTime(
                      gameData.game_date,
                      gameData.game_time,
                      gameData.game_timezone || 'UTC'
                    );
                    return formatted.date;
                  } catch (error) {
                    return format(new Date(gameData.game_date), 'MMMM dd, yyyy');
                  }
                })()}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Clock className="h-4 w-4 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Time</p>
              <p className="font-medium text-foreground">
                {(() => {
                  try {
                    const formatted = formatGameDateTime(
                      gameData.game_date,
                      gameData.game_time,
                      gameData.game_timezone || 'UTC'
                    );
                    return formatted.time;
                  } catch (error) {
                    return gameData.game_time;
                  }
                })()}
              </p>
            </div>
          </div>
        </div>

        {/* Venue Information */}
        {showVenueInfo && (
          <div className="bg-muted/30 p-4 rounded-lg border-l-4 border-l-primary">
            <div className="flex items-center gap-2 mb-3">
              <Building className="h-5 w-5 text-primary" />
              <h4 className="font-semibold text-foreground">Venue Information</h4>
            </div>
            
            {loading ? (
              <div className="text-sm text-muted-foreground">Loading venue details...</div>
            ) : venueInfo ? (
              <div className="space-y-2">
                <div className="flex items-start gap-3">
                  <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="font-medium text-foreground">{venueInfo.venue_name}</p>
                    {venueInfo.venue_address && (
                      <p className="text-sm text-muted-foreground">{venueInfo.venue_address}</p>
                    )}
                    {(venueInfo.city || venueInfo.state_province) && (
                      <p className="text-sm text-muted-foreground">
                        {venueInfo.city}{venueInfo.city && venueInfo.state_province ? ', ' : ''}{venueInfo.state_province}
                      </p>
                    )}
                  </div>
                </div>

                {/* Venue Contact Info */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                  {venueInfo.phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-foreground">{venueInfo.phone}</span>
                    </div>
                  )}
                  {venueInfo.email && (
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-foreground">{venueInfo.email}</span>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">
                Venue: {gameData.venue_name || gameData.field_location || 'TBD'}
              </div>
            )}
          </div>
        )}

        {/* Teams */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Home Team */}
          <div className="bg-muted/20 p-3 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Users className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-foreground">Home Team</span>
            </div>
            {gameData.home_team ? (
              <div className="flex items-center gap-2">
                {gameData.home_team.team_logo && (
                  <img 
                    src={gameData.home_team.team_logo} 
                    alt="Team logo"
                    className="w-6 h-6 rounded-full object-cover"
                  />
                )}
                <span className="font-medium text-foreground">{gameData.home_team.team_name}</span>
                {gameData.home_team_status === 'accepted' && (
                  <CheckCircle className="h-4 w-4 text-green-600" />
                )}
              </div>
            ) : (
              <span className="text-muted-foreground">TBD</span>
            )}
          </div>

          {/* Away Team */}
          <div className="bg-muted/20 p-3 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Users className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-foreground">Away Team</span>
            </div>
            {gameData.away_team ? (
              <div className="flex items-center gap-2">
                {gameData.away_team.team_logo && (
                  <img 
                    src={gameData.away_team.team_logo} 
                    alt="Team logo"
                    className="w-6 h-6 rounded-full object-cover"
                  />
                )}
                <span className="font-medium text-foreground">{gameData.away_team.team_name}</span>
                {gameData.away_team_status === 'accepted' && (
                  <CheckCircle className="h-4 w-4 text-green-600" />
                )}
              </div>
            ) : (
              <span className="text-muted-foreground">TBD</span>
            )}
          </div>
        </div>

        {/* Sport & Additional Info */}
        <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-1">
            <Trophy className="h-4 w-4" />
            <span>{gameData.sport?.sport_name || 'Basketball'}</span>
          </div>
          {gameData.minimum_players_required && (
            <div className="flex items-center gap-1">
              <Users className="h-4 w-4" />
              <span>Min. {gameData.minimum_players_required} players</span>
            </div>
          )}
          {gameData.scorekeeper_needed && (
            <div className="flex items-center gap-1">
              <AlertTriangle className="h-4 w-4" />
              <span>Scorekeeper needed</span>
            </div>
          )}
        </div>

        {/* Confirmation Status */}
        {(gameData.home_team_status || gameData.away_team_status) && (
          <div className="bg-primary/5 p-3 rounded-lg border border-primary/20">
            <h5 className="font-medium text-foreground mb-2">Team Confirmations</h5>
            <div className="space-y-1 text-sm">
              {gameData.home_team_status && (
                <div className="flex items-center justify-between">
                  <span>Home Team:</span>
                  <Badge 
                    variant={gameData.home_team_status === 'accepted' ? 'default' : 'secondary'}
                    className="text-xs"
                  >
                    {gameData.home_team_status === 'accepted' ? 'Confirmed' : 'Pending'}
                  </Badge>
                </div>
              )}
              {gameData.away_team_status && (
                <div className="flex items-center justify-between">
                  <span>Away Team:</span>
                  <Badge 
                    variant={gameData.away_team_status === 'accepted' ? 'default' : 'secondary'}
                    className="text-xs"
                  >
                    {gameData.away_team_status === 'accepted' ? 'Confirmed' : 'Pending'}
                  </Badge>
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};